import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const recommendationCache = new Map();
const maxRecommendations = 6;
let preferredProductId = '';

class JesoliCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-product-ids'];

  #abortController = null;
  #drawer = null;

  connectedCallback() {
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);

    if (this.#drawer?.hasAttribute('open')) this.#load();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-ids' || oldValue === newValue || !this.isConnected) return;
    if (this.#drawer?.hasAttribute('open')) this.#load();
  }

  #handleDrawerOpen = () => this.#load();

  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const productIds = this.#getItemProductIds(detail?.items);
        const eventProductId = this.#normalizeProductId(detail?.productId);

        if (eventProductId && productIds.includes(eventProductId)) {
          preferredProductId = eventProductId;
        } else if (!productIds.includes(preferredProductId)) {
          preferredProductId = productIds[0] || '';
        }

        const orderedProductIds = this.#prioritizeProductIds(productIds);
        const serializedProductIds = JSON.stringify(orderedProductIds);

        if (this.dataset.productIds !== serializedProductIds) {
          this.dataset.productIds = serializedProductIds;
        } else if (this.#drawer?.hasAttribute('open')) {
          this.#load();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-recommendations] Cart update failed:', error);
      });
  };

  async #load() {
    const productIds = this.#prioritizeProductIds(this.#getDatasetProductIds());
    if (productIds.length === 0 || !this.dataset.url) {
      this.hidden = true;
      return;
    }

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;
    this.setAttribute('aria-busy', 'true');

    try {
      const cartProductIds = new Set(productIds);

      for (const productId of productIds) {
        const results = await this.#fetchResults(productId, abortController.signal);
        const filteredResults = this.#filterResults(results, cartProductIds);

        if (filteredResults) {
          this.replaceChildren(filteredResults);
          this.hidden = false;
          return;
        }
      }

      this.replaceChildren();
      this.hidden = true;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.replaceChildren();
      this.hidden = true;
      console.warn('[cart-recommendations] Recommendations unavailable:', error);
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  async #fetchResults(productId, signal) {
    const requestUrl = new URL(this.dataset.url, window.location.origin);
    requestUrl.searchParams.set('product_id', productId);
    const cacheKey = requestUrl.toString();
    let html = recommendationCache.get(cacheKey);

    if (!html) {
      const response = await fetch(cacheKey, { signal });
      if (!response.ok) throw new Error(`Recommendation request failed with ${response.status}`);
      html = await response.text();
      recommendationCache.set(cacheKey, html);
    }

    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const results = responseDocument.querySelector('[data-cart-recommendations-results]');
    return results instanceof HTMLElement ? results : null;
  }

  #filterResults(results, cartProductIds) {
    if (!(results instanceof HTMLElement)) return null;

    const filteredResults = results.cloneNode(true);
    if (!(filteredResults instanceof HTMLElement)) return null;

    const list = filteredResults.querySelector('[data-cart-recommendations-list]');
    if (!(list instanceof HTMLElement)) return null;

    const seenProductIds = new Set();
    const cards = Array.from(list.querySelectorAll('[data-cart-recommendations-card]'));
    let visibleCount = 0;

    for (const card of cards) {
      const productId = this.#normalizeProductId(card.getAttribute('data-product-id'));
      const shouldRemove =
        !productId ||
        cartProductIds.has(productId) ||
        seenProductIds.has(productId) ||
        visibleCount >= maxRecommendations;

      if (shouldRemove) {
        card.remove();
        continue;
      }

      seenProductIds.add(productId);
      visibleCount += 1;
    }

    return visibleCount > 0 ? filteredResults : null;
  }

  #getDatasetProductIds() {
    try {
      const productIds = JSON.parse(this.dataset.productIds || '[]');
      return Array.isArray(productIds) ? this.#getItemProductIds(productIds) : [];
    } catch {
      return [];
    }
  }

  #getItemProductIds(items) {
    if (!Array.isArray(items)) return [];

    const productIds = items
      .map((item) => this.#normalizeProductId(item?.product_id ?? item))
      .filter(Boolean);

    return [...new Set(productIds)];
  }

  #prioritizeProductIds(productIds) {
    if (!preferredProductId || !productIds.includes(preferredProductId)) return productIds;
    return [preferredProductId, ...productIds.filter((productId) => productId !== preferredProductId)];
  }

  #normalizeProductId(productId) {
    return productId == null ? '' : `${productId}`;
  }
}

if (!customElements.get('jesoli-cart-recommendations')) {
  customElements.define('jesoli-cart-recommendations', JesoliCartRecommendations);
}
